/**
 * KABIL API Service
 * Service for fetching KABIL (Khanij Bidesh India Limited) data
 */

import { API_BASE_URL as KABIL_API_BASE } from './apiConfig';

/**
 * Fetch KABIL deals with optional filtering
 * @param {Object} filters - Optional filters for mineral, stage, country
 * @returns {Promise<Object>} KABIL deals data
 */
export const getKabilDeals = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams();
    
    if (filters.mineral) queryParams.append('mineral', filters.mineral);
    if (filters.stage) queryParams.append('stage', filters.stage);
    if (filters.country) queryParams.append('country', filters.country);
    if (filters.iso3) queryParams.append('iso3', filters.iso3);
    
    const url = `${KABIL_API_BASE}/kabil/deals${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching KABIL deals:', error);
    return { count: 0, items: [] };
  }
};

function normalizeKabilMinerals(minerals) {
  if (minerals == null) return [];
  if (Array.isArray(minerals)) return minerals.map(String).filter(Boolean);
  if (typeof minerals === 'object') {
    return Object.values(minerals)
      .flat()
      .map(String)
      .filter(Boolean);
  }
  return [];
}

/**
 * Build modal-friendly kabilAgreement object from /kabil/deals items (newest first from API).
 */
export function buildKabilAgreementFromDeals(items) {
  if (!items?.length) return null;
  const primary = items[0];
  const mineralSet = new Set();
  items.forEach((deal) => {
    normalizeKabilMinerals(deal.minerals).forEach((m) => mineralSet.add(m));
  });
  const mineralsList = [...mineralSet];
  const detailsParts = [];
  if (primary.quantity_tpa != null && primary.quantity_tpa !== '') {
    const q = Number(primary.quantity_tpa);
    detailsParts.push(
      Number.isFinite(q) ? `Quantity: ${q.toLocaleString()} tpa` : `Quantity: ${primary.quantity_tpa} tpa`
    );
  }
  if (primary.signed_date) detailsParts.push(`Latest signed: ${primary.signed_date}`);
  if (items.length > 1) detailsParts.push(`${items.length} deal records`);
  return {
    status: primary.status || 'KABIL engagement',
    stage: primary.stage || 'Not applicable',
    type: 'KABIL partnership',
    details: detailsParts.length ? detailsParts.join(' · ') : 'KABIL partnership data on file',
    partner: primary.partner || undefined,
    minerals: mineralsList.length ? mineralsList : undefined,
  };
}

/**
 * Resolve KABIL deals for a map GeoJSON feature (ISO3 first, then country name).
 */
export async function fetchKabilDealsForMapCountry(countryFeature) {
  const props = countryFeature?.properties || {};
  const rawIso = props['ISO3166-1-Alpha-3'] || props.ISO_A3 || props.ADM0_A3;
  const iso3 =
    rawIso && /^[A-Za-z]{3}$/.test(String(rawIso).trim())
      ? String(rawIso).trim().toUpperCase()
      : null;
  const countryName = props.name || props.NAME || props.ADMIN;

  if (iso3) {
    const byIso = await getKabilDeals({ iso3 });
    if (byIso?.items?.length) return byIso;
  }
  if (countryName && countryName !== 'Unknown') {
    return getKabilDeals({ country: countryName });
  }
  return { count: 0, items: [] };
}

/**
 * Fetch KABIL statistics
 * @returns {Promise<Object>} KABIL statistics data
 */
export const getKabilStats = async () => {
  try {
    const response = await fetch(`${KABIL_API_BASE}/kabil/stats`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching KABIL stats:', error);
    return {
      totals: { deals: 0, countries: 0, quantity_tpa: 0 },
      by_mineral: {},
      by_stage: {},
      by_country: {}
    };
  }
};

/**
 * Fetch KABIL timeline data
 * @param {string} mineral - Optional mineral filter
 * @returns {Promise<Object>} KABIL timeline data
 */
export const getKabilTimeline = async (mineral = null) => {
  try {
    const url = mineral 
      ? `${KABIL_API_BASE}/kabil/timeline?mineral=${encodeURIComponent(mineral)}`
      : `${KABIL_API_BASE}/kabil/timeline`;
    
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching KABIL timeline:', error);
    return { series: [], events: [] };
  }
};

/**
 * Get recent KABIL developments for dashboard display
 * @param {string} mineral - Optional mineral filter
 * @returns {Promise<Array>} Array of recent KABIL developments
 */
export const getRecentKabilDevelopments = async (mineral = null) => {
  try {
    // Get recent deals from the timeline
    const timeline = await getKabilTimeline(mineral);
    
    if (!timeline.events || timeline.events.length === 0) {
      return [];
    }
    
    // Sort events by date (most recent first) and take the latest 5
    const recentEvents = timeline.events
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .slice(0, 10)
      .map(event => ({
        id: event.id,
        date: new Date(event.date).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        }),
        country: event.country,
        iso3: event.iso3, // Include ISO3 code for flag mapping
        stage: event.stage,
        minerals: event.minerals,
        partner: event.partner,
        quantity_tpa: event.quantity_tpa,
        // Format date for display
        formattedDate: new Date(event.date).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        })
      }));
    
    return recentEvents;
  } catch (error) {
    console.error('Error fetching recent KABIL developments:', error);
    return [];
  }
};

/**
 * Get KABIL deal summary for dashboard
 * @returns {Promise<Object>} KABIL summary data
 */
export const getKabilSummary = async () => {
  try {
    const stats = await getKabilStats();
    
    return {
      totalDeals: stats.totals.deals,
      totalCountries: stats.totals.countries,
      totalQuantity: stats.totals.quantity_tpa,
      topMinerals: Object.entries(stats.by_mineral)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 3)
        .map(([mineral, count]) => ({ mineral, count })),
      topCountries: Object.entries(stats.by_country)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 3)
        .map(([country, count]) => ({ country, count }))
    };
  } catch (error) {
    console.error('Error fetching KABIL summary:', error);
    return {
      totalDeals: 0,
      totalCountries: 0,
      totalQuantity: 0,
      topMinerals: [],
      topCountries: []
    };
  }
};

/**
 * Get country flag code from country name or ISO3 code
 * @param {string} countryName - Country name or ISO3 code
 * @returns {string} Country flag code (2-letter ISO code)
 */
export const getCountryFlagCode = (countryName) => {
  // First check if it's already a 2-letter code
  if (countryName && countryName.length === 2) {
    return countryName.toUpperCase();
  }
  
  // Map from country names to 2-letter ISO codes
  const countryNameMap = {
    'Argentina': 'AR',
    'Australia': 'AU',
    'Zambia': 'ZM',
    'Chile': 'CL',
    'Brazil': 'BR',
    'China': 'CN',
    'India': 'IN',
    'Japan': 'JP',
    'United States': 'US',
    'Canada': 'CA',
    'South Africa': 'ZA',
    'Democratic Republic of the Congo': 'CD',
    'Indonesia': 'ID',
    'Philippines': 'PH',
    'Russia': 'RU',
    'New Caledonia': 'NC',
    'Madagascar': 'MG',
    'Bolivia': 'BO',
    'Cuba': 'CU'
  };
  
  // Map from ISO3 codes to 2-letter ISO codes
  const iso3ToIso2Map = {
    'ARG': 'AR',
    'AUS': 'AU',
    'ZMB': 'ZM',
    'CHL': 'CL',
    'BRA': 'BR',
    'CHN': 'CN',
    'IND': 'IN',
    'USA': 'US',
    'CAN': 'CA',
    'ZAF': 'ZA',
    'COD': 'CD',
    'IDN': 'ID',
    'PHL': 'PH',
    'RUS': 'RU',
    'NCL': 'NC',
    'MDG': 'MG',
    'BOL': 'BO',
    'CUB': 'CU',
    'JPN': 'JP',
    'DEU': 'DE',
    'FRA': 'FR',
    'GBR': 'GB',
    'KOR': 'KR',
    'MYS': 'MY',
    'SGP': 'SG',
    'THA': 'TH',
    'VNM': 'VN',
    'BGD': 'BD',
    'LKA': 'LK',
    'NPL': 'NP',
    'PAK': 'PK',
    'AFG': 'AF',
    'IRN': 'IR',
    'IRQ': 'IQ',
    'SAU': 'SA',
    'ARE': 'AE',
    'QAT': 'QA',
    'KWT': 'KW',
    'BHR': 'BH',
    'OMN': 'OM',
    'YEM': 'YE',
    'JOR': 'JO',
    'LBN': 'LB',
    'SYR': 'SY',
    'TUR': 'TR',
    'ISR': 'IL',
    'PSE': 'PS',
    'CYP': 'CY',
    'MLT': 'MT',
    'GRC': 'GR',
    'BGR': 'BG',
    'ROU': 'RO',
    'MDA': 'MD',
    'UKR': 'UA',
    'BLR': 'BY',
    'LTU': 'LT',
    'LVA': 'LV',
    'EST': 'EE',
    'FIN': 'FI',
    'SWE': 'SE',
    'NOR': 'NO',
    'DNK': 'DK',
    'ISL': 'IS',
    'IRL': 'IE',
    'PRT': 'PT',
    'ESP': 'ES',
    'ITA': 'IT',
    'CHE': 'CH',
    'AUT': 'AT',
    'HUN': 'HU',
    'SVK': 'SK',
    'CZE': 'CZ',
    'POL': 'PL',
    'SVN': 'SI',
    'HRV': 'HR',
    'BIH': 'BA',
    'SRB': 'RS',
    'MNE': 'ME',
    'MKD': 'MK',
    'ALB': 'AL',
    'KOS': 'XK'
  };
  
  // Check if it's a country name first
  if (countryNameMap[countryName]) {
    return countryNameMap[countryName];
  }
  
  // Check if it's an ISO3 code
  if (iso3ToIso2Map[countryName]) {
    return iso3ToIso2Map[countryName];
  }
  
  // Return UN as fallback
  return 'UN';
};

/**
 * Get stage color for UI display
 * @param {string} stage - Deal stage
 * @returns {string} CSS color class
 */
export const getStageColor = (stage) => {
  const colorMap = {
    'MoU': 'text-blue-400',
    'Exploration': 'text-green-400',
    'Feasibility Study': 'text-yellow-400',
    'Due Diligence': 'text-orange-400',
    'JV Signed': 'text-red-400',
    'Mining License': 'text-purple-400',
    'Production': 'text-pink-400'
  };
  
  return colorMap[stage] || 'text-gray-400';
};

/**
 * Get stage priority for sorting
 * @param {string} stage - Deal stage
 * @returns {number} Priority number (higher = more important)
 */
export const getStagePriority = (stage) => {
  const priorityMap = {
    'MoU': 1,
    'Exploration': 2,
    'Feasibility Study': 3,
    'Due Diligence': 4,
    'JV Signed': 5,
    'Mining License': 6,
    'Production': 7
  };
  
  return priorityMap[stage] || 0;
};
